import { useMemo, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import StackChart from "../../../components/graphs/StackChart";
import BarChart from "../../../components/graphs/BarChart";
import { useTranslation } from "react-i18next";
import useGetCaseDelaySeverityQuery from "../hooks/useGetCaseDelaySeverityQuery";
import BarGraphLoader from "../../../components/skeletonLoaders/BarGraphLoader";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import useFilterValue from "../hooks/useFilterValue";
import { buildQueryStringFromFilters } from "../../../helpers/buildQueryStringFromFilters";
import { CASE_DETAIL } from "../types/CommonTypes";
import StackChartLoader from "../../../components/skeletonLoaders/StackChartLoader";
//@ts-ignore
import { useScreenshot } from "use-react-screenshot";
import CustomTooltip from "../../../components/CustomTooltip";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
import useGetCasesPerOtQuery from "../hooks/useGetCasesPerOtQuery";
import useGetAvgCasesPerActiveDayQuery from "../hooks/useGetAvgCasesPerActiveDayQuery";
import useGetAvgCaseDurationQuery from "../hooks/useGetAvgCaseDurationQuery";
import useGetAvgTurnoverTimeQuery from "../hooks/useGetAvgTurnoverTimeQuery";
import useGetOnTimeStartEndPercentageQuery from "../hooks/useGetOnTimeStartEndPercentageQuery";
import DynamicTable from "../../../components/dynamicTable/DynamicTable";
import { TABLE_HEADER_ARRAY } from "../../../utils/Types";
import { GET_ON_TIME_START_END_PERCENTAGE_RESPONSE } from "../types/ResponseTypes";
import useGetOtUtilizationQuery from "../hooks/useGetOtUtilizationQuery";
import ChartContainer, {
  RenderChartProps,
} from "../../../components/ChartContainer";
import useDashboardExport from "../hooks/useDashboardExport";
import useStableChartData from "../../../hooks/useStableChartData";

interface MasterDataTableProps {
  exportOverviewScreenshot?: any;
}

// Table row type for On-Time Start/End Percentage
export type OTOnTimeStartEndRow = {
  otName: string;
  totalCases: number;
  percentOnTimeStarts: string;
  percentOnTimeEnds: string;
};

// Table headers for DynamicTable
const otOnTimeStartEndHeaders: TABLE_HEADER_ARRAY = [
  { key: "otName", label: "OT Name",position: "left" },
  { key: "totalCases", label: "Total Cases", position: "center" },
  { key: "percentOnTimeStarts", label: "% On-Time Starts", position: "center" },
  { key: "percentOnTimeEnds", label: "% On-Time Ends", position: "center" },
];

// Helper to map API response to table rows
function mapOnTimeStartEndRows(
  data: GET_ON_TIME_START_END_PERCENTAGE_RESPONSE
): OTOnTimeStartEndRow[] {
  return (data || []).map((row) => ({
    otName: row.otName,
    totalCases: row.totalCases,
    percentOnTimeStarts: `${row.percentOnTimeStarts?.toFixed(0)}%`,
    percentOnTimeEnds: `${row.percentOnTimeEnds?.toFixed(0)}%`,
  }));
}

// Extract data fetching hooks into a custom hook
const useOverviewData = (
  tenantId: string | undefined,
  hospitalId: string | undefined,
  queryString: string | undefined
) => {
  const { data: caseDelaySeverityData, isLoading: isCaseDelaySeverity } =
    useGetCaseDelaySeverityQuery(tenantId ?? "", hospitalId ?? "", queryString);

  const { data: casesPerOtData, isLoading: isGettingCasesPerOt } =
    useGetCasesPerOtQuery(tenantId ?? "", hospitalId ?? "", queryString);

  const {
    data: averageCasesPerActiveDayData,
    isLoading: isGettingAverageCasesPerActiveDay,
  } = useGetAvgCasesPerActiveDayQuery(
    tenantId ?? "",
    hospitalId ?? "",
    queryString
  );

  const { data: avgCaseDurationData, isLoading: isGettingAvgCaseDuration } =
    useGetAvgCaseDurationQuery(tenantId ?? "", hospitalId ?? "", queryString);

  const { data: avgTurnoverTimeData, isLoading: isGettingAvgTurnoverTime } =
    useGetAvgTurnoverTimeQuery(tenantId ?? "", hospitalId ?? "", queryString);

  const { data: otUtilizationData, isLoading: isGettingOtUtilization } =
    useGetOtUtilizationQuery(tenantId ?? "", hospitalId ?? "", queryString);

  const {
    data: onTimeStartEndPercentageData,
    isLoading: isGettingOnTimeStartEndPercentage,
  } = useGetOnTimeStartEndPercentageQuery(
    tenantId ?? "",
    hospitalId ?? "",
    queryString
  );

  return {
    caseDelaySeverityData,
    isCaseDelaySeverity,
    casesPerOtData,
    isGettingCasesPerOt,
    averageCasesPerActiveDayData,
    isGettingAverageCasesPerActiveDay,
    avgCaseDurationData,
    isGettingAvgCaseDuration,
    avgTurnoverTimeData,
    isGettingAvgTurnoverTime,
    otUtilizationData,
    isGettingOtUtilization,
    onTimeStartEndPercentageData,
    isGettingOnTimeStartEndPercentage,
  };
};

const OTByOTAnalysisDashboard: React.FC<MasterDataTableProps> = ({
  exportOverviewScreenshot,
}) => {
  const { t } = useTranslation();
  const currentHospital = useSelectedHospitalValue();
  const { tenantId: authTenantId, hospitalId: authHospitalId } = useAuthValue();
  const tenantId = currentHospital?.tenantId ?? authTenantId;
  const hospitalId = currentHospital?.uuid ?? authHospitalId;
  const screenshotRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const filters = useFilterValue();
  const queryString = buildQueryStringFromFilters(filters);
  const [, takeScreenshot] = useScreenshot();

  const {
    caseDelaySeverityData,
    isCaseDelaySeverity,
    casesPerOtData,
    isGettingCasesPerOt,
    averageCasesPerActiveDayData,
    isGettingAverageCasesPerActiveDay,
    avgCaseDurationData,
    isGettingAvgCaseDuration,
    avgTurnoverTimeData,
    isGettingAvgTurnoverTime,
    otUtilizationData,
    isGettingOtUtilization,
    onTimeStartEndPercentageData,
    isGettingOnTimeStartEndPercentage,
  } = useOverviewData(tenantId, hospitalId, queryString);

  const otOnTimeStartEndRows = useMemo(
    () => mapOnTimeStartEndRows(onTimeStartEndPercentageData || []),
    [onTimeStartEndPercentageData]
  );

  // Transform casesPerOtData for BarChart
  const casesPerOtBarChartData = useMemo(() => {
    if (!casesPerOtData || !Array.isArray(casesPerOtData)) return [];
    return casesPerOtData.map((item: any) => ({
      label: item.otName,
      value: item.caseCount,
    }));
  }, [casesPerOtData]);

  // Transform averageCasesPerActiveDayData for BarChart
  // Falls back to the last non-empty result for this hospital so a transient
  // empty refetch doesn't flash "No graph to display" until the next poll.
  const averageCasesPerActiveDayBarChartData = useStableChartData(
    averageCasesPerActiveDayData,
    (item: any) => ({ label: item.otName, value: item.avgCasesPerDay }),
    hospitalId
  );

  // Transform avgCaseDurationData for BarChart
  const avgCaseDurationBarChartData = useMemo(() => {
    if (!avgCaseDurationData || !Array.isArray(avgCaseDurationData)) return [];
    return avgCaseDurationData.map((item: any) => ({
      label: item.otName,
      value: item.avgCaseDuration,
    }));
  }, [avgCaseDurationData]);

  // Transform avgTurnoverTimeData for BarChart
  const avgTurnoverTimeBarChartData = useMemo(() => {
    if (!avgTurnoverTimeData || !Array.isArray(avgTurnoverTimeData)) return [];
    return avgTurnoverTimeData.map((item: any) => ({
      label: item.otName,
      value: item.avgTurnoverTime,
    }));
  }, [avgTurnoverTimeData]);

  // Transform otUtilizationData for BarChart
  const otUtilizationBarChartData = useMemo(() => {
    if (!otUtilizationData || !Array.isArray(otUtilizationData)) return [];
    return otUtilizationData.map((item: any) => ({
      label: item.otName,
      value: item.utilizationPercentage,
    }));
  }, [otUtilizationData]);

  const getFilteredCaseDelaySeverity = useMemo(() => {
    if (!caseDelaySeverityData) return [];
    return caseDelaySeverityData
      .filter((item) => item?.totalDelayMinutes > 0.9)
      .map((item) => ({
        ...item,
        cases: item?.cases?.reduce((x, y) => {
          if (y?.delayMinutes > 0.9) {
            x.push({
              ...y,
              index: x?.length + 1,
            });
          }
          return x;
        }, [] as Array<CASE_DETAIL>),
      }))
      .sort((a, b) => {
        const numA = parseInt(a.otName.replace(/[^\d]/g, ""), 10);
        const numB = parseInt(b.otName.replace(/[^\d]/g, ""), 10);
        return numA - numB;
      });
  }, [caseDelaySeverityData]);

  useDashboardExport({
    exportRef: exportOverviewScreenshot,
    screenshotRef,
    setIsExporting,
    takeScreenshot,
    filename: "orva-otbyot-analysis.pdf",
  });

  const renderChart = ({
    data,
    title,
    tooltipTitle,
    isLoading,
    props,
  }: RenderChartProps) => {
    if (isLoading) {
      return <BarGraphLoader {...props} />;
    }

    return (
      <BarChart
        key={hospitalId}
        title={t(title)}
        tooltipTitle={t(tooltipTitle)}
        data={data}
        fromOTByOT={true}
        {...props}
      />
    );
  };

  return (
    <Box ref={screenshotRef} flex={1} py={2} sx={{ px: isExporting ? 2 : 0 }}>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingCasesPerOt}
            data={casesPerOtBarChartData}
            title="OT_Total_Cases"
            tooltipTitle="OT_Total_Cases_Tooltip"
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingAverageCasesPerActiveDay}
            data={averageCasesPerActiveDayBarChartData ?? []}
            title="OT_Average_Cases_Per_Active_Day"
            tooltipTitle="OT_Average_Cases_Per_Active_Day_Tooltip"
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingAvgCaseDuration}
            data={avgCaseDurationBarChartData ?? []}
            title="OT_Average_Case_Duration"
            tooltipTitle="OT_Average_Case_Duration_Tooltip"
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingAvgTurnoverTime}
            data={avgTurnoverTimeBarChartData ?? []}
            title="OT_Average_Turnover_Time"
            tooltipTitle="OT_Average_Turnover_Time_Tooltip"
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingOtUtilization}
            data={otUtilizationBarChartData ?? []}
            title="OT_Utilization"
            tooltipTitle="OT_Utilization_Tooltip"
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            height="23rem"
            borderRadius={2}
            bgcolor="custom.background.primary"
            p={3}
          >
            <CustomTooltip title={t("OT_On_Time_Starts_Ends_Tooltip")}>
              <Typography
                variant="h6"
                color="custom.foreground.secondary"
                mb={1}
              >
                {t("OT_On_Time_Starts_Ends")}
              </Typography>
            </CustomTooltip>
            <DynamicTable
              headers={otOnTimeStartEndHeaders}
              rowData={otOnTimeStartEndRows}
              isLoading={isGettingOnTimeStartEndPercentage}
              tableContainerProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: "none",
                  maxHeight: 330,
                  p: 0,
                },
              }}
              tableProps={{
                size: "small",
                "aria-label": "on-time start end table",
                sx: {
                  background: "transparent",
                  "& thead th": {
                    color: "#1E1B39",
                    fontWeight: "bold",
                    fontSize: 14,
                    border: "none",
                  },
                  "& thead th:first-of-type": {
                    paddingLeft: 0,
                  },
                  "& td": {
                    fontSize: 14,
                    color: "#1D1B20",
                    paddingLeft: 0,
                  },
                },
              }}
              stickyHeader
              tableHeight={330}
              disableSorting={false}
              transparentRows={true}
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <Box
            height="auto"
            borderRadius={2}
            bgcolor="custom.background.primary"
            p={3}
          >
            {!isCaseDelaySeverity ? (
              <StackChart
                stackData={getFilteredCaseDelaySeverity || []}
                chartTitle={t("Case_Delay_Severity_Avg")}
                tooltipTitle={t("Case_Delay_Severity_Avg_Tooltip")}
                showTotal={false}
              />
            ) : (
              <StackChartLoader numberOfBars={6} numberOfStacks={5} />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OTByOTAnalysisDashboard;
